import { MultiselectStyledChip } from '@global-dax-ad-platform/dax-components';
import { ChipProps } from '@global-engineering-shared/gweb-material-types';
import { Typography } from '@global-engineering-shared/gweb-material-ui';
import { ReactNode, useMemo } from 'react';

import useStyles from 'features/EntityDetails/components/EntityDetails/ChipListDetail/ChipListDetail.styles';
import {
  getAllItemsToDisplay,
  ItemStatus,
} from 'features/EntityDetails/components/EntityDetails/ChipListDetail/utils/ChipListDetailUtils';

type ChipListDetailProps<T> = {
  headerText: string;
  items: T[] | null;
  previousItems?: T[] | null;
  getKey: (item: T) => string;
  getLabel: (item: T) => ReactNode;
  getTooltip?: (item: T) => string[];
  getChipClasses?: (item: T) => ChipProps['classes'] | undefined;
};

type ChipWithStatusProps<T> = {
  item: T;
  status: ItemStatus;
};

const ChipListDetail = <T,>({
  headerText,
  items,
  previousItems,
  getKey,
  getLabel,
  getTooltip,
  getChipClasses,
}: ChipListDetailProps<T>) => {
  const classes = useStyles({ itemStatus: undefined });
  const allItems = useMemo(
    () => getAllItemsToDisplay(items ?? [], getKey, previousItems),
    [items, previousItems, getKey]
  );

  const ChipWithStatus = ({ item, status }: ChipWithStatusProps<T>) => {
    const classesWithStatus = useStyles({ itemStatus: status });
    return (
      <MultiselectStyledChip
        key={getKey(item)}
        className={classesWithStatus.chip}
        classes={getChipClasses ? getChipClasses(item) : undefined}
        label={getLabel(item)}
        tooltip={getTooltip ? getTooltip(item) : undefined}
      />
    );
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" fontWeight={400} className={classes.header}>
        {headerText}
      </Typography>
      <div className={classes.chipContainer}>
        {allItems.map(({ item, status }) => {
          return <ChipWithStatus item={item} status={status} />;
        })}
      </div>
    </div>
  );
};

export default ChipListDetail;
